/**
 * Global keyboard shortcuts for cinema mode. Call once in AppLayout.
 *
 * Shortcuts:
 * - Ctrl/Cmd + Shift + H → toggle cinema mode (hide/show all UI)
 * - Escape               → exit cinema mode
 *
 * Note on Escape: `useSidebarShortcuts` also binds Escape, and its handler fires
 * whenever the sidebar is `hidden` — which is exactly the state cinema mode puts
 * it in. That hook defers to this one (it bails when cinema is active), so the
 * two never both act on a single press.
 */
import { useEffect } from 'react'
import { useCinemaStore } from '@/stores/cinemaStore'

export function useCinemaShortcuts() {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip when typing in inputs
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      const modifier = e.metaKey || e.ctrlKey

      if (modifier && e.shiftKey && (e.key === 'H' || e.key === 'h')) {
        e.preventDefault()
        useCinemaStore.getState().toggle()
      } else if (e.key === 'Escape') {
        if (useCinemaStore.getState().active) {
          e.preventDefault()
          useCinemaStore.getState().exit()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])
}
